/**
 * Organize API client
 */

import {
  actions,
  type User,
  type MixEvent,
  type MixParticipant,
  type MixUpload,
  type MixProduction,
  type OutputTarget,
  type CreateEventInput,
} from "./store";

const API_BASE = "/api";

// =============================================================================
// Helpers
// =============================================================================

async function apiFetch<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options?.headers || {}),
    },
    ...options,
  });
  if (!res.ok) {
    const body = await res.text();
    let message = `Request failed: ${res.status}`;
    try {
      const json = JSON.parse(body);
      message = json.error || message;
    } catch {
      // keep default
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

// =============================================================================
// Auth API
// =============================================================================

export async function checkSession(): Promise<{ user: User | null }> {
  try {
    const res = await fetch(`${API_BASE}/auth/session`, {
      credentials: "include",
    });
    if (!res.ok) return { user: null };
    const data = (await res.json()) as { user?: User };
    return { user: data.user ?? null };
  } catch {
    return { user: null };
  }
}

export async function sendCode(email: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/auth/send-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
      credentials: "include",
    });
    const data = (await res.json()) as { ok?: boolean; error?: string };
    if (!res.ok) {
      return { ok: false, error: data.error || "Failed to send code" };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Network error" };
  }
}

export async function verifyCode(email: string, code: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/auth/verify-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code }),
      credentials: "include",
    });
    const data = (await res.json()) as { success?: boolean; error?: string };
    if (!res.ok) {
      return { ok: false, error: data.error || "Invalid code" };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Network error" };
  }
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
  actions.logout();
}

// =============================================================================
// Events API
// =============================================================================

export async function listEvents(): Promise<MixEvent[]> {
  return apiFetch<MixEvent[]>("/events");
}

export async function getEvent(id: string): Promise<MixEvent> {
  return apiFetch<MixEvent>(`/events/${id}`);
}

export async function createEvent(data: CreateEventInput): Promise<MixEvent> {
  return apiFetch<MixEvent>("/events", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateEvent(
  id: string,
  data: Partial<CreateEventInput>
): Promise<MixEvent> {
  return apiFetch<MixEvent>(`/events/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteEvent(id: string): Promise<void> {
  await apiFetch<{ success: boolean }>(`/events/${id}`, {
    method: "DELETE",
  });
}

export async function activateEvent(id: string): Promise<void> {
  await apiFetch<{ success: boolean }>(`/events/${id}/activate`, {
    method: "POST",
  });
}

export async function closeEvent(id: string): Promise<void> {
  await apiFetch<{ success: boolean }>(`/events/${id}/close`, {
    method: "POST",
  });
}

// =============================================================================
// Participants API
// =============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

export async function getParticipants(
  eventId: string,
  params?: { limit?: number; offset?: number }
): Promise<PaginatedResponse<MixParticipant>> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.offset) searchParams.set("offset", String(params.offset));
  const qs = searchParams.toString();
  return apiFetch<PaginatedResponse<MixParticipant>>(
    `/events/${eventId}/participants${qs ? `?${qs}` : ""}`
  );
}

// =============================================================================
// Uploads API
// =============================================================================

export async function getUploads(
  eventId: string,
  params?: { limit?: number; offset?: number; status?: string }
): Promise<PaginatedResponse<MixUpload>> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.offset) searchParams.set("offset", String(params.offset));
  if (params?.status) searchParams.set("status", params.status);
  const qs = searchParams.toString();
  return apiFetch<PaginatedResponse<MixUpload>>(
    `/events/${eventId}/uploads${qs ? `?${qs}` : ""}`
  );
}

export async function moderateUpload(
  eventId: string,
  uploadId: string,
  status: "approved" | "rejected",
  note?: string
): Promise<void> {
  await apiFetch<{ success: boolean }>(`/events/${eventId}/uploads`, {
    method: "PATCH",
    body: JSON.stringify({ uploadId, moderationStatus: status, moderationNote: note }),
  });
}

export async function reprocessUpload(
  eventId: string,
  uploadId: string
): Promise<void> {
  await apiFetch<{ success: boolean }>(
    `/events/${eventId}/uploads/${uploadId}/reprocess`,
    { method: "POST" }
  );
}

export async function bulkModerate(
  eventId: string,
  uploadIds: string[],
  status: "approved" | "rejected"
): Promise<void> {
  await apiFetch<{ success: boolean }>(`/events/${eventId}/bulk-moderate`, {
    method: "POST",
    body: JSON.stringify({ uploadIds, moderationStatus: status }),
  });
}

export interface AiReviewResult {
  uploadId: string;
  fileId: string;
  score: number;
  reasoning: string;
  recommendation: string;
}

export async function aiReviewUploads(
  eventId: string
): Promise<{ results: AiReviewResult[] }> {
  return apiFetch<{ results: AiReviewResult[] }>(`/events/${eventId}/ai-review`, {
    method: "POST",
  });
}

// =============================================================================
// Upload API (Organizer multipart upload)
// =============================================================================

export async function requestUpload(
  eventId: string,
  data: { filename: string; contentType: string; sizeBytes: number }
): Promise<{ fileId: string; participantId: string; chunkSize: number }> {
  return apiFetch(`/events/${eventId}/upload/request`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function createMultipartUpload(
  eventId: string,
  fileId: string
): Promise<{ uploadId: string }> {
  return apiFetch(`/events/${eventId}/upload/${fileId}/multipart/create`, {
    method: "POST",
  });
}

export async function uploadPart(
  eventId: string,
  fileId: string,
  uploadId: string,
  partNumber: number,
  body: Blob
): Promise<{ etag: string }> {
  const res = await fetch(
    `${API_BASE}/events/${eventId}/upload/${fileId}/multipart/part?uploadId=${encodeURIComponent(uploadId)}&partNumber=${partNumber}`,
    {
      method: "PUT",
      credentials: "include",
      body,
    }
  );
  if (!res.ok) {
    const text = await res.text();
    let message = `Upload part failed: ${res.status}`;
    try {
      message = JSON.parse(text).error || message;
    } catch {
      // keep default
    }
    throw new Error(message);
  }
  return res.json() as Promise<{ etag: string }>;
}

export async function completeMultipartUpload(
  eventId: string,
  fileId: string,
  data: {
    uploadId: string;
    parts: { partNumber: number; etag: string }[];
    participantId: string;
    sizeBytes: number;
  }
): Promise<{ success: boolean; uploadId: string }> {
  return apiFetch(`/events/${eventId}/upload/${fileId}/multipart/complete`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// =============================================================================
// Production API
// =============================================================================

export async function getCreditEstimate(
  eventId: string,
  targets: OutputTarget[]
): Promise<{ totalCredits: number; perOutput: { style: string; credits: number }[] }> {
  return apiFetch(`/events/${eventId}/credit-estimate`, {
    method: "POST",
    body: JSON.stringify({ targets }),
  });
}

export async function triggerProduction(
  eventId: string,
  targets: OutputTarget[],
  prompts?: Record<number, string>
): Promise<MixProduction[]> {
  return apiFetch<MixProduction[]>(`/events/${eventId}/produce`, {
    method: "POST",
    body: JSON.stringify({ targets, prompts }),
  });
}

export async function getProductions(
  eventId: string
): Promise<MixProduction[]> {
  return apiFetch<MixProduction[]>(`/events/${eventId}/productions`);
}

export async function retryProduction(
  eventId: string,
  productionId: string
): Promise<{ success: boolean; jobId: string }> {
  return apiFetch(`/events/${eventId}/productions/${productionId}/retry`, {
    method: "POST",
  });
}

export async function deleteProduction(
  eventId: string,
  productionId: string
): Promise<void> {
  await apiFetch<{ success: boolean }>(
    `/events/${eventId}/productions/${productionId}/delete`,
    { method: "POST" }
  );
}
